import React from 'react';
import type {
  TierEvaluationAudit,
  NextTierInfo,
} from 'src/api/rebates/types/auditTypes';
import { formatCurrency } from '@erp/shared-utils';
import { CheckCircleIcon, CircleIcon } from '@phosphor-icons/react';

interface TierProgressBarProps {
  tierEvaluation: TierEvaluationAudit;
  qualifyingVolume: number;
  nextTier?: NextTierInfo;
}

export function TierProgressBar({
  tierEvaluation,
  qualifyingVolume,
  nextTier,
}: TierProgressBarProps) {
  const { allTiers, activeTier, isRetroactive } = tierEvaluation;

  if (allTiers.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">No tiers configured</div>
    );
  }

  // Sort tiers by threshold for display
  const sortedTiers = [...allTiers].sort((a, b) => a.threshold - b.threshold);

  // Calculate progress for visual display
  const maxThreshold =
    nextTier?.threshold || sortedTiers[sortedTiers.length - 1].threshold;
  const progressPercentage = Math.min(
    (qualifyingVolume / maxThreshold) * 100,
    100,
  );

  return (
    <div className="space-y-4">
      {/* Visual Progress Bar */}
      <div className="relative">
        {/* Background track */}
        <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
          {/* Progress fill */}
          <div
            className="h-full bg-gradient-to-r from-green-500 to-green-600 transition-all duration-500"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>

        {/* Tier markers */}
        <div className="relative h-8 mt-2">
          {sortedTiers.map((tier) => {
            const position = (tier.threshold / maxThreshold) * 100;
            const isCurrent = activeTier?.tierOrder === tier.tierOrder;

            return (
              <div
                key={tier.tierOrder}
                className="absolute -translate-x-1/2"
                style={{ left: `${Math.min(position, 98)}%` }}
              >
                <div className="flex flex-col items-center">
                  {tier.isMet ? (
                    <CheckCircleIcon
                      className={`h-6 w-6 ${
                        isCurrent
                          ? 'text-green-600 fill-green-100'
                          : 'text-green-500'
                      }`}
                    />
                  ) : (
                    <CircleIcon
                      className={`h-6 w-6 ${
                        isCurrent ? 'text-gray-600' : 'text-gray-400'
                      }`}
                    />
                  )}
                  <div className="text-xs mt-1 whitespace-nowrap">
                    Tier {tier.tierOrder}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Current Volume Indicator */}
      <div className="text-sm">
        <p className="font-medium">
          Current Volume: {formatCurrency(qualifyingVolume)}
        </p>
      </div>

      {/* Tier Details Table */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-2 font-medium">Tier</th>
              <th className="text-right p-2 font-medium">Threshold</th>
              <th className="text-right p-2 font-medium">Rebate %</th>
              <th className="text-center p-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {sortedTiers.map((tier) => {
              const isCurrent = activeTier?.tierOrder === tier.tierOrder;

              return (
                <tr
                  key={tier.tierOrder}
                  className={`
                    ${isCurrent ? 'bg-green-50 font-medium' : ''}
                    ${tier.isMet && !isCurrent ? 'bg-gray-50' : ''}
                  `}
                >
                  <td className="p-2">
                    Tier {tier.tierOrder}
                    {isCurrent && (
                      <span className="ml-2 text-xs text-green-600">
                        (Active)
                      </span>
                    )}
                  </td>
                  <td className="p-2 text-right">
                    {formatCurrency(tier.threshold)}
                  </td>
                  <td className="p-2 text-right">{tier.rebatePercentage}%</td>
                  <td className="p-2 text-center">
                    {tier.isMet ? (
                      <CheckCircleIcon className="h-4 w-4 text-green-600 inline" />
                    ) : (
                      <CircleIcon className="h-4 w-4 text-gray-400 inline" />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Retroactive Indicator */}
      {isRetroactive && activeTier && (
        <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
          <p className="text-sm text-purple-900">
            <span className="font-semibold">Retroactive Calculation:</span> The{' '}
            {activeTier.rebatePercentage}% rate applies to the entire qualifying
            volume of {formatCurrency(qualifyingVolume)}.
          </p>
        </div>
      )}
    </div>
  );
}
